import { useRouterState } from '@tanstack/react-router';
import { useLayoutEffect, useRef, type RefObject } from 'react';
import { prefersReducedMotion } from '@shigo-ui/ui';

import { fluidDuration, fluidEasing } from './fluidMotion';

/** Tabs inside one details page are the same page: switching them does not replay the entrance. */
export function pageEntranceKey(pathname: string) {
  return pathname.replace(/\/details(?:\/.*)?$/, '/details');
}

/**
 * Plays one subtle compositor-only entrance (opacity + a 6px rise) when a different page renders.
 * It starts in a layout effect, in the same commit that renders the new page, so the page is
 * never painted fully opaque first (which read as a second animation). Keyed by page, so
 * redirects and details tabs count once and late data never replays it; nothing remounts.
 * Reduced motion keeps a short fade without travel.
 */
export function usePageEntrance(target: RefObject<HTMLElement | null>) {
  const pageKey = useRouterState({
    select: (state) => pageEntranceKey(state.location.pathname),
  });
  const previousKey = useRef<string | null>(null);

  useLayoutEffect(() => {
    const element = target.current;
    const isFirstPage = previousKey.current === null;
    const changed = previousKey.current !== pageKey;
    previousKey.current = pageKey;
    if (isFirstPage || !changed || !element || typeof element.animate !== 'function') return;
    const reduced = prefersReducedMotion();
    const animation = element.animate(
      reduced
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [
            // A short rise; the shell's clip box keeps it from overflowing the scroll area.
            { opacity: 0, transform: 'translateY(6px)' },
            { opacity: 1, transform: 'none' },
          ],
      reduced
        ? { duration: 150, easing: 'linear' }
        : { duration: fluidDuration, easing: fluidEasing },
    );
    return () => {
      animation.cancel();
    };
  }, [pageKey, target]);
}
